// The study tools built on public-domain text: cross-references, parallel
// translations and Strong's. All of it is static reference data, so it is
// cached hard — a chapter's parallel text is fetched once and reused by every
// block on the page, the same way the ESV chapter is.
import type {
  ChapterTextView,
  CrossRefsView,
  StrongsView,
  TranslationId,
  TranslationStatus,
  VerseWordsView,
  WordUsageView,
} from "@bibleunleashed/shared";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

/** Reference data does not go stale within a session. */
const forever = { staleTime: 24 * 3_600_000, gcTime: 24 * 3_600_000 } as const;

const ref = (osis: string) => encodeURIComponent(osis);

export function useTranslations() {
  return useQuery({
    queryKey: ["study", "translations"],
    queryFn: () => api<TranslationStatus[]>("/api/study/translations"),
    ...forever,
  });
}

/** A whole chapter (or any range) in one public-domain translation. */
export function useChapterText(translation: TranslationId | undefined, osis: string | undefined) {
  return useQuery({
    queryKey: ["study", "text", translation ?? "", osis ?? ""],
    queryFn: () => api<ChapterTextView>(`/api/study/text/${translation}/${ref(osis!)}`),
    enabled: Boolean(translation && osis),
    ...forever,
  });
}

export function useCrossRefs(verse: string | undefined, translation: TranslationId = "BSB") {
  return useQuery({
    queryKey: ["study", "crossrefs", verse ?? "", translation],
    queryFn: () => api<CrossRefsView>(`/api/study/crossrefs/${ref(verse!)}?translation=${translation}`),
    enabled: Boolean(verse),
    ...forever,
  });
}

export function useVerseWords(verse: string | undefined) {
  return useQuery({
    queryKey: ["study", "words", verse ?? ""],
    queryFn: () => api<VerseWordsView>(`/api/study/words/${ref(verse!)}`),
    enabled: Boolean(verse),
    ...forever,
  });
}

export function useStrongs(id: string | null) {
  return useQuery({
    queryKey: ["study", "strongs", id ?? ""],
    queryFn: () => api<StrongsView>(`/api/study/strongs/${id}`),
    enabled: Boolean(id),
    ...forever,
  });
}

/** How often a Strong's number is used, book by book or, given a book, chapter by chapter: what a word chart draws. */
export function useWordUsage(id: string, book: string | null) {
  return useQuery({
    queryKey: ["study", "usage", id, book ?? ""],
    queryFn: () => api<WordUsageView>(`/api/study/usage/${id}${book ? `?in=${encodeURIComponent(book)}` : ""}`),
    ...forever,
  });
}
